'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';
import dynamic from 'next/dynamic';

const QRCode = dynamic(() => import('qrcode.react').then(m => m.QRCodeSVG), { ssr: false });

const supabase = createClient();

type TabType = 'qr_scanner' | 'qr_stampa' | 'firma_digitale';

interface Device {
  id: string;
  nome: string;
  tipo: string;
  ip_address: string;
  numero_seriale: string;
  reparto: string;
}

interface QrEtichetta {
  id: string;
  device_id: string;
  qr_data: string;
  formato: string;
  stampato: boolean;
  created_at: string;
}

interface FirmaDigitale {
  id: string;
  firmato_da_nome: string;
  firmato_da_email: string;
  firma_base64: string;
  ticket_id: string | null;
  created_at: string;
}

const FORMATI = ['A4', 'A5', 'etichetta_62mm', 'etichetta_29mm'];

// Native canvas signature pad component
function SignaturePad({ onSave, onClear }: { onSave: (dataUrl: string) => void; onClear: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const lastPos = useRef<{ x: number; y: number } | null>(null);

  const getPos = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if ('touches' in e) {
      return { x: ((e as React.TouchEvent).touches[0].clientX - rect.left) * scaleX, y: ((e as React.TouchEvent).touches[0].clientY - rect.top) * scaleY };
    }
    return { x: ((e as React.MouseEvent).clientX - rect.left) * scaleX, y: ((e as React.MouseEvent).clientY - rect.top) * scaleY };
  };

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    isDrawing.current = true;
    lastPos.current = getPos(e, canvas);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    e.preventDefault();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current!.x, lastPos.current!.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    lastPos.current = pos;
  };

  const stopDraw = () => { isDrawing.current = false; lastPos.current = null; };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onClear();
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onSave(canvas.toDataURL('image/png'));
  };

  return (
    <div>
      <div className="border-2 border-dashed border-border rounded-xl overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          width={460}
          height={200}
          className="w-full touch-none cursor-crosshair"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
        />
      </div>
      <div className="flex gap-2 mt-2">
        <button onClick={clearCanvas} className="text-xs text-muted-color hover:text-white transition-colors">Cancella</button>
        <button onClick={saveSignature} className="text-xs text-sky-400 hover:text-sky-300 transition-colors ml-auto">Salva firma</button>
      </div>
    </div>
  );
}

export default function QrFirmaContent() {
  const [activeTab, setActiveTab] = useState<TabType>('qr_stampa');
  const [devices, setDevices] = useState<Device[]>([]);
  const [etichette, setEtichette] = useState<QrEtichetta[]>([]);
  const [firme, setFirme] = useState<FirmaDigitale[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [formato, setFormato] = useState('A4');
  const [generating, setGenerating] = useState(false);
  const [scanResult, setScanResult] = useState('');
  const [scanInput, setScanInput] = useState('');
  const [firmaForm, setFirmaForm] = useState({ nome: '', email: '', ticket_id: '' });
  const [firmaBase64, setFirmaBase64] = useState('');
  const [savingFirma, setSavingFirma] = useState(false);
  const [firmaSalvata, setFirmaSalvata] = useState(false);
  const { profile } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: d }, { data: e }, { data: f }] = await Promise.all([
      supabase.from('devices').select('id, nome, tipo, ip_address, numero_seriale, reparto').order('nome').limit(100),
      supabase.from('qr_etichette').select('*').order('created_at', { ascending: false }).limit(50),
      supabase.from('firme_digitali').select('*').order('created_at', { ascending: false }).limit(20),
    ]);
    setDevices(d || []);
    setEtichette(e || []);
    setFirme(f || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const generateQrEtichetta = async () => {
    if (!selectedDevice) return;
    setGenerating(true);
    let qrData = JSON.stringify({
      id: selectedDevice.id,
      nome: selectedDevice.nome,
      tipo: selectedDevice.tipo,
      ip: selectedDevice.ip_address,
      sn: selectedDevice.numero_seriale,
      url: `${window.location.origin}/device-inventory?id=${selectedDevice.id}`,
    });
    await supabase.from('qr_etichette').insert({
      device_id: selectedDevice.id,
      qr_data: qrData,
      formato,
      stampato_da: profile?.id,
      struttura_id: null,
    });
    setGenerating(false);
    fetchData();
  };

  const printQr = (etichetta: QrEtichetta) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    let qrData: any = {};
    try { qrData = JSON.parse(etichetta.qr_data); } catch {}
    printWindow.document.write(`
      <html><head><title>Etichetta QR - ${qrData.nome || 'Dispositivo'}</title>
      <style>
        body { font-family: Arial, sans-serif; display: flex; flex-direction: column; align-items: center; padding: 20px; }
        .label { border: 1px solid #ccc; padding: 15px; text-align: center; max-width: 300px; }
        h3 { margin: 10px 0 5px; font-size: 14px; }
        p { margin: 2px 0; font-size: 11px; color: #666; }
        @media print { body { margin: 0; } }
      </style></head>
      <body>
        <div class="label">
          <div style="width:150px;height:150px;border:1px dashed #ccc;margin:0 auto;display:flex;align-items:center;justify-content:center;font-size:10px;color:#999;">QR Code</div>
          <h3>${qrData.nome || 'Dispositivo'}</h3>
          <p>Tipo: ${qrData.tipo || 'N/A'}</p>
          <p>IP: ${qrData.ip || 'N/A'}</p>
          <p>S/N: ${qrData.sn || 'N/A'}</p>
          <p style="font-size:9px;margin-top:8px;">GestioneLab · gestionelab.com</p>
        </div>
        <script>window.onload = () => { window.print(); window.close(); }</script>
      </body></html>
    `);
    printWindow.document.close();
    supabase.from('qr_etichette').update({ stampato: true, stampato_at: new Date().toISOString() }).eq('id', etichetta.id);
  };

  const handleScanInput = () => {
    if (!scanInput.trim()) return;
    try {
      const data = JSON.parse(scanInput);
      setScanResult(JSON.stringify(data, null, 2));
      if (data.id) {
        const device = devices.find(d => d.id === data.id);
        if (device) setSelectedDevice(device);
      }
    } catch {
      setScanResult(scanInput);
    }
    setScanInput('');
  };

  const saveFirma = async () => {
    if (!firmaBase64 || !firmaForm.nome) return;
    setSavingFirma(true);
    await supabase.from('firme_digitali').insert({
      firma_base64: firmaBase64,
      firmato_da_nome: firmaForm.nome,
      firmato_da_email: firmaForm.email,
      ticket_id: firmaForm.ticket_id || null,
      ip_address: '',
      struttura_id: null,
    });
    setFirmaBase64('');
    setFirmaForm({ nome: '', email: '', ticket_id: '' });
    setFirmaSalvata(true);
    setTimeout(() => setFirmaSalvata(false), 3000);
    setSavingFirma(false);
    fetchData();
  };

  const tabs = [
    { id: 'qr_stampa' as TabType, label: 'Stampa Etichette QR', icon: 'QrCodeIcon' },
    { id: 'qr_scanner' as TabType, label: 'Scanner QR', icon: 'CameraIcon' },
    { id: 'firma_digitale' as TabType, label: 'Firma Digitale', icon: 'PencilSquareIcon' },
  ];

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'QR Code e Firma Digitale' }]} />
      <div>
        <h1 className="text-2xl font-bold text-white">QR Code e Firma Digitale</h1>
        <p className="text-text-secondary text-sm mt-1">Stampa etichette QR per dispositivi, scansiona QR e raccogli firme digitali</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-sky-500 text-sky-400' : 'border-transparent text-muted-color hover:text-white'}`}>
            <Icon name={tab.icon as any} size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : activeTab === 'qr_stampa' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Generator */}
          <div className="bg-surface-elevated border border-border rounded-xl p-5">
            <h3 className="font-semibold text-white mb-4">Genera Etichetta QR</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-muted-color mb-1">Dispositivo</label>
                <select value={selectedDevice?.id || ''} onChange={e => setSelectedDevice(devices.find(d => d.id === e.target.value) || null)}
                  className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-white">
                  <option value="">Seleziona dispositivo...</option>
                  {devices.map(d => <option key={d.id} value={d.id}>{d.nome} ({d.tipo})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-muted-color mb-1">Formato Etichetta</label>
                <select value={formato} onChange={e => setFormato(e.target.value)}
                  className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-white">
                  {FORMATI.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              {selectedDevice && (
                <div className="flex flex-col items-center gap-3 p-4 bg-white rounded-xl">
                  <QRCode
                    value={JSON.stringify({ id: selectedDevice.id, nome: selectedDevice.nome, tipo: selectedDevice.tipo, ip: selectedDevice.ip_address, sn: selectedDevice.numero_seriale, url: `${typeof window !== 'undefined' ? window.location.origin : ''}/device-inventory?id=${selectedDevice.id}` })}
                    size={150}
                    level="M"
                  />
                  <div className="text-center text-black">
                    <p className="font-bold text-sm">{selectedDevice.nome}</p>
                    <p className="text-xs text-gray-600">{selectedDevice.tipo} · {selectedDevice.ip_address || 'N/A'}</p>
                  </div>
                </div>
              )}
              <button onClick={generateQrEtichetta} disabled={!selectedDevice || generating}
                className="w-full bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-semibold transition-colors">
                {generating ? 'Generazione...' : 'Genera e Salva Etichetta'}
              </button>
            </div>
          </div>

          {/* History */}
          <div className="bg-surface-elevated border border-border rounded-xl p-5">
            <h3 className="font-semibold text-white mb-4">Etichette Generate ({etichette.length})</h3>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {etichette.length === 0 ? (
                <p className="text-muted-color text-sm text-center py-4">Nessuna etichetta generata</p>
              ) : etichette.map(et => {
                let qrData: any = {};
                try { qrData = JSON.parse(et.qr_data); } catch {}
                return (
                  <div key={et.id} className="flex items-center gap-3 p-3 bg-surface rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{qrData.nome || 'Dispositivo'}</p>
                      <p className="text-xs text-muted-color">{et.formato} · {new Date(et.created_at).toLocaleDateString('it-IT')}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {et.stampato && <span className="text-xs text-green-400">✓ Stampato</span>}
                      <button onClick={() => printQr(et)}
                        className="flex items-center gap-1 text-xs bg-surface-elevated hover:bg-surface border border-border text-white px-2 py-1 rounded-lg transition-colors">
                        <Icon name="PrinterIcon" size={12} /> Stampa
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : activeTab === 'qr_scanner' ? (
        <div className="max-w-lg mx-auto space-y-4">
          <div className="bg-surface-elevated border border-border rounded-xl p-5">
            <h3 className="font-semibold text-white mb-4">Scanner QR Code</h3>
            <div className="bg-surface rounded-xl p-4 mb-4 text-center">
              <Icon name="CameraIcon" size={48} className="mx-auto mb-3 text-muted-color opacity-50" />
              <p className="text-sm text-muted-color mb-2">Usa la fotocamera del dispositivo mobile per scansionare</p>
              <p className="text-xs text-muted-color">Oppure incolla il contenuto del QR qui sotto</p>
            </div>
            <div className="flex gap-2">
              <input value={scanInput} onChange={e => setScanInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleScanInput()}
                className="flex-1 bg-surface border border-border rounded-lg px-3 py-2 text-sm text-white"
                placeholder="Incolla contenuto QR o scansiona..." />
              <button onClick={handleScanInput} className="bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                Analizza
              </button>
            </div>
            {scanResult && (
              <div className="mt-4 p-3 bg-surface rounded-lg">
                <p className="text-xs text-muted-color mb-1">Risultato scansione:</p>
                <pre className="text-xs text-green-400 overflow-auto">{scanResult}</pre>
                {selectedDevice && (
                  <div className="mt-3 p-3 bg-sky-500/10 border border-sky-500/20 rounded-lg">
                    <p className="text-sm font-semibold text-white">{selectedDevice.nome}</p>
                    <p className="text-xs text-muted-color">{selectedDevice.tipo} · {selectedDevice.reparto || 'N/A'}</p>
                    <a href={`/device-inventory?id=${selectedDevice.id}`}
                      className="text-xs text-sky-400 hover:underline mt-1 inline-block">
                      Apri scheda dispositivo →
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="max-w-lg mx-auto space-y-4">
          <div className="bg-surface-elevated border border-border rounded-xl p-5">
            <h3 className="font-semibold text-white mb-4">Raccolta Firma Digitale</h3>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm text-muted-color mb-1">Nome Firmatario *</label>
                <input value={firmaForm.nome} onChange={e => setFirmaForm(p => ({ ...p, nome: e.target.value }))}
                  className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-white" placeholder="Mario Rossi" />
              </div>
              <div>
                <label className="block text-sm text-muted-color mb-1">Email</label>
                <input value={firmaForm.email} onChange={e => setFirmaForm(p => ({ ...p, email: e.target.value }))}
                  className="w-full bg-surface border border-border rounded-lg px-3 py-2 text-sm text-white" placeholder="mario@ospedale.it" />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-muted-color mb-2">Firma qui sotto</label>
              <SignaturePad
                onSave={(dataUrl) => setFirmaBase64(dataUrl)}
                onClear={() => setFirmaBase64('')}
              />
              {firmaBase64 && (
                <div className="mt-2 p-2 bg-green-500/10 border border-green-500/20 rounded-lg text-xs text-green-400 flex items-center gap-1">
                  <Icon name="CheckCircleIcon" size={12} /> Firma acquisita
                </div>
              )}
            </div>

            {firmaSalvata && (
              <div className="mb-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm flex items-center gap-2">
                <Icon name="CheckCircleIcon" size={16} /> Firma salvata con successo!
              </div>
            )}

            <button onClick={saveFirma} disabled={savingFirma || !firmaForm.nome || !firmaBase64}
              className="w-full bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-semibold transition-colors">
              {savingFirma ? 'Salvataggio...' : 'Salva Firma Digitale'}
            </button>
          </div>

          {/* Firme salvate */}
          {firme.length > 0 && (
            <div className="bg-surface-elevated border border-border rounded-xl p-5">
              <h3 className="font-semibold text-white mb-3">Firme Salvate ({firme.length})</h3>
              <div className="space-y-2">
                {firme.slice(0, 5).map(f => (
                  <div key={f.id} className="flex items-center gap-3 p-3 bg-surface rounded-lg">
                    {f.firma_base64 && (
                      <img src={f.firma_base64} alt="Firma" className="h-8 w-16 object-contain bg-white rounded" />
                    )}
                    <div>
                      <p className="text-sm text-white">{f.firmato_da_nome}</p>
                      <p className="text-xs text-muted-color">{new Date(f.created_at).toLocaleString('it-IT')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
